import * as THREE from 'three'

/** Efeitos leves de condução: marcas de pneu, fumaça de derrapagem e partículas de nitro. */
export default class DrivingEffects {
    constructor(experience) {
        this.experience = experience
        this.scene = experience.scene
        this.clock = 0
        this.lastMark = 0
        this.lastSmoke = 0
        this.lastNitro = 0
        this.marks = []
        this.particles = []
        this.maxMarks = 180
        this.maxParticles = 44
        this.markGeometry = new THREE.PlaneGeometry(0.22, 0.72)
        this.smokeGeometry = new THREE.SphereGeometry(0.12, 7, 6)
        this.markMaterial = new THREE.MeshBasicMaterial({ color: 0x111315, transparent: true, opacity: 0.36, depthWrite: false, side: THREE.DoubleSide })
        this.smokeMaterial = new THREE.MeshBasicMaterial({ color: 0xc5c8ca, transparent: true, opacity: 0.32, depthWrite: false })
        this.nitroMaterial = new THREE.MeshBasicMaterial({ color: 0x44dfff, transparent: true, opacity: 0.9, depthWrite: false })
        this.tmpRight = new THREE.Vector3()
        this.tmpForward = new THREE.Vector3()
        this.tmpUp = new THREE.Vector3(0, 1, 0)
    }

    update(car, deltaMs = 16) {
        if (!car?.vehicle?.chassisBody || !car?.chassisMesh) return
        const dt = Math.min(0.05, Math.max(0, deltaMs / 1000))
        this.clock += dt
        const body = car.vehicle.chassisBody
        const v = body.velocity
        const speed = Math.hypot(v.x, v.z)
        this.tmpForward.set(0, 0, 1).applyQuaternion(car.chassisMesh.quaternion).setY(0).normalize()
        this.tmpRight.set(1, 0, 0).applyQuaternion(car.chassisMesh.quaternion).setY(0).normalize()
        const lateral = Math.abs(v.x * this.tmpRight.x + v.z * this.tmpRight.z)
        const drifting = speed > 7.5 && lateral > 3.1 && body.position.y < 3.5
        const now = this.clock

        if (drifting && now - this.lastMark > 0.075) {
            this.lastMark = now
            this.addSkid(car, -1)
            this.addSkid(car, 1)
        }
        if (drifting && now - this.lastSmoke > 0.12) {
            this.lastSmoke = now
            this.addParticle(car, 'smoke', -1)
            this.addParticle(car, 'smoke', 1)
        }

        const boosting = this.experience.items && Date.now() < (this.experience.items.boostUntil || 0) && !this.experience.items.stunned
        if (boosting && speed > 2 && now - this.lastNitro > 0.045) {
            this.lastNitro = now
            this.addParticle(car, 'nitro', -1)
            this.addParticle(car, 'nitro', 1)
        }
        this.updateMarks(dt)
        this.updateParticles(dt)
    }

    addSkid(car, side) {
        if (this.marks.length >= this.maxMarks) {
            const old = this.marks.shift()
            this.scene.remove(old.mesh)
            old.mesh.material.dispose()
        }
        const p = car.chassisMesh.position
        const q = car.chassisMesh.quaternion
        const local = new THREE.Vector3(side * 0.86, -1.25, -1.05).applyQuaternion(q)
        const mesh = new THREE.Mesh(this.markGeometry, this.markMaterial.clone())
        mesh.position.set(p.x + local.x, 0.045, p.z + local.z)
        mesh.rotation.set(-Math.PI / 2, 0, -Math.atan2(this.tmpForward.x, this.tmpForward.z))
        mesh.renderOrder = 2
        this.scene.add(mesh)
        this.marks.push({ mesh, age: 0, life: 8.5 })
    }

    addParticle(car, type, side) {
        if (this.particles.length >= this.maxParticles) {
            const old = this.particles.shift()
            this.scene.remove(old.mesh)
            old.mesh.material.dispose()
        }
        const q = car.chassisMesh.quaternion
        const p = car.chassisMesh.position
        const local = new THREE.Vector3(side * 0.82, type === 'nitro' ? -0.12 : -0.5, -1.75).applyQuaternion(q)
        const material = (type === 'nitro' ? this.nitroMaterial : this.smokeMaterial).clone()
        const mesh = new THREE.Mesh(this.smokeGeometry, material)
        mesh.position.set(p.x + local.x, Math.max(0.12, p.y + local.y), p.z + local.z)
        const s = type === 'nitro' ? 0.45 : 0.45 + Math.random() * 0.3
        mesh.scale.set(s, s, s)
        this.scene.add(mesh)
        const backward = this.tmpForward.clone().multiplyScalar(type === 'nitro' ? -5.5 : -1.2)
        this.particles.push({ mesh, type, age: 0, life: type === 'nitro' ? 0.32 : 0.72,
            vx: backward.x + (Math.random() - 0.5) * 1.2,
            vz: backward.z + (Math.random() - 0.5) * 1.2,
            vy: type === 'nitro' ? 0.1 : 0.45 + Math.random() * 0.5 })
    }

    updateMarks(dt) {
        for (let i = this.marks.length - 1; i >= 0; i--) {
            const mark = this.marks[i]
            mark.age += dt
            mark.mesh.material.opacity = 0.36 * Math.max(0, 1 - mark.age / mark.life)
            if (mark.age >= mark.life) {
                this.scene.remove(mark.mesh)
                mark.mesh.material.dispose()
                this.marks.splice(i, 1)
            }
        }
    }

    updateParticles(dt) {
        for (let i = this.particles.length - 1; i >= 0; i--) {
            const p = this.particles[i]
            p.age += dt
            p.mesh.position.x += p.vx * dt
            p.mesh.position.y += p.vy * dt
            p.mesh.position.z += p.vz * dt
            const progress = Math.min(1, p.age / p.life)
            p.mesh.material.opacity = (p.type === 'nitro' ? 0.9 : 0.32) * (1 - progress)
            const scale = p.type === 'nitro' ? 0.45 * (1 - progress * 0.7) : 0.45 + progress * 1.3
            p.mesh.scale.setScalar(scale)
            if (p.age >= p.life) {
                this.scene.remove(p.mesh)
                p.mesh.material.dispose()
                this.particles.splice(i, 1)
            }
        }
    }

    destroy() {
        for (const item of this.marks) { this.scene.remove(item.mesh); item.mesh.material.dispose() }
        for (const item of this.particles) { this.scene.remove(item.mesh); item.mesh.material.dispose() }
        this.marks.length = 0
        this.particles.length = 0
        this.markGeometry.dispose()
        this.smokeGeometry.dispose()
        this.markMaterial.dispose()
        this.smokeMaterial.dispose()
        this.nitroMaterial.dispose()
    }
}
